import React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../lib/cn";
import { useDensity } from "../../theme/ThemeContext";
import type { Density } from "../../theme/types";

const button = cva(
  [
    "inline-flex items-center justify-center rounded-md font-semibold transition-colors",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/40 focus-visible:ring-offset-2",
    "disabled:opacity-60 disabled:cursor-not-allowed",
  ],
  {
    variants: {
      variant: {
        primary: "bg-accent-600 text-on-accent hover:bg-accent-500",
        secondary: "border border-accent-600 text-accent-600 bg-transparent hover:bg-accent-50",
        ghost: "text-accent-600 bg-transparent hover:bg-accent-50",
        destructive: "bg-error-solid text-error-on-solid hover:opacity-90",
        link: "text-accent-600 underline-offset-4 hover:underline p-0",
      },
      density: {
        comfortable: "px-5 py-2.5 text-sm gap-2",
        compact: "px-3 py-1.5 text-xs gap-1.5",
      },
    },
    compoundVariants: [{ variant: "link", density: ["comfortable", "compact"], className: "px-0 py-0" }],
    defaultVariants: { variant: "primary", density: "comfortable" },
  }
);

type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  Omit<VariantProps<typeof button>, "density"> & { density?: Density };

export function Button({ variant, density, className = "", children, ...props }: ButtonProps) {
  const resolvedDensity = useDensity(density);
  return (
    <button className={cn(button({ variant, density: resolvedDensity }), className)} {...props}>
      {children}
    </button>
  );
}
